import React from 'react';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <a href="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-6 text-sm font-medium" data-testid="back-link">
          <ArrowLeft size={16} /> Back to SHALEM
        </a>

        <div className="bg-white rounded-2xl shadow-sm border p-8 space-y-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900" data-testid="privacy-title">Privacy Policy</h1>
            <p className="text-gray-500 mt-2">SHALEM - Government Services Platform</p>
            <p className="text-gray-400 text-sm mt-1">Last updated: April 13, 2026</p>
          </div>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              SHALEM ("we", "our", or "the Application") is a platform designed to centralize administrative services and bill payments in Israel. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our application.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">2. Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed">We collect the following categories of personal information:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li><strong>Identity Information:</strong> Teudat Zehut (Israeli ID number), full name, date of birth</li>
              <li><strong>Contact Information:</strong> Address, phone number, email address</li>
              <li><strong>Family Information:</strong> Children's names and school details (for education bill management)</li>
              <li><strong>Financial Information:</strong> Bill amounts, payment history, payment method details (processed securely through Stripe)</li>
              <li><strong>Usage Data:</strong> App usage patterns, language preferences, appointment history</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">3. How We Use Your Information</h2>
            <p className="text-gray-600 leading-relaxed">Your information is used to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li>Authenticate your identity and provide secure access to your account</li>
              <li>Display and manage your bills from various government agencies</li>
              <li>Process payments securely through our payment partners</li>
              <li>Schedule appointments with administrative offices</li>
              <li>Update your address across multiple government organizations simultaneously</li>
              <li>Send reminders about upcoming bills and appointments</li>
              <li>Provide multilingual support in 10 languages</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">4. Data Security</h2>
            <p className="text-gray-600 leading-relaxed">
              We implement industry-standard security measures to protect your personal data:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li>256-bit SSL/TLS encryption for all data transmission</li>
              <li>Encrypted storage for sensitive personal information</li>
              <li>Secure authentication using JWT tokens with expiration</li>
              <li>Payment processing handled exclusively by PCI DSS-compliant Stripe</li>
              <li>Regular security audits and vulnerability assessments</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">5. Data Sharing</h2>
            <p className="text-gray-600 leading-relaxed">
              We share your information only with the following parties, and only as necessary to provide our services:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li><strong>Government agencies:</strong> To process address changes, appointments, and bill inquiries as requested by you</li>
              <li><strong>Payment processors:</strong> Stripe, for secure payment processing only</li>
              <li><strong>Service providers:</strong> Infrastructure and hosting providers, under strict data protection agreements</li>
            </ul>
            <p className="text-gray-600 leading-relaxed">
              We do <strong>not</strong> sell, rent, or trade your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">6. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your personal data for as long as your account is active and as required by applicable Israeli law. Payment records are retained for 7 years as required by Israeli tax regulations. You may request deletion of your account and associated data at any time.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed">Under Israeli Privacy Protection Law (1981) and applicable regulations, you have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
              <li>Access your personal data stored in our systems</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal data</li>
              <li>Object to certain data processing activities</li>
              <li>Export your data in a portable format</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">8. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              SHALEM manages children's school bills through parent accounts only. We do not knowingly collect personal information directly from children under 18. All children's data is managed through and accessible only by their linked parent or guardian account.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">9. Cookies and Local Storage</h2>
            <p className="text-gray-600 leading-relaxed">
              The application uses local storage (not cookies) to maintain your session and language preferences. No third-party tracking cookies are used. Analytics data, if collected, is anonymized and used solely for improving the application.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">10. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes through the application. Continued use of SHALEM after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-800">11. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              For questions about this Privacy Policy or to exercise your data rights, please contact us:
            </p>
            <div className="bg-blue-50 rounded-xl p-4 text-gray-700">
              <p><strong>SHALEM - Privacy Team</strong></p>
              <p>Email: privacy@shalem.co.il</p>
              <p>Address: Israel</p>
            </div>
          </section>

          <div className="border-t pt-6 mt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2026 SHALEM. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
